<template>
    <v-container  v-resize="onResize">
        <statistical-header :title="title"></statistical-header>
        <p>Data displayed represents the gender composition on the opening day of each Parliament.</p>
        <loader v-if="loading" />
        <template v-if="!loading">
            <statistical-member-or-senator-selector :value="selectedMpOrSenator" @input="selectedMpOrSenator = $event" />
            <br/>

            <v-layout row wrap align-center justify-space-between>
                <v-btn-toggle v-model="chartType" class="transparent handbook_toggleButton" :mandatory="true">
                    <v-tooltip top :disabled="this.$vuetify.breakpoint.name !== 'md'">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" class="handbook_button" :value="'genderComposition'"><span class="long-button-text chamberButton">Gender composition in Parliament</span></v-btn>
                        </template>
                        <span>Gender composition in Parliament</span>
                    </v-tooltip>
                    <v-tooltip top :disabled="this.$vuetify.breakpoint.name !== 'md'">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" class="handbook_button" :value="'womenInParliament'"><span class="long-button-text chamberButton">Women in Parliament</span></v-btn>
                        </template>
                        <span>Women in Parliament</span>
                    </v-tooltip>
                    <v-tooltip top :disabled="this.$vuetify.breakpoint.name !== 'md'">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" class="handbook_button" :value="'byParliament'"><span class="long-button-text chamberButton">Women in Parliament by party</span></v-btn>
                        </template>
                        <span>Women in Parliament by party</span>
                    </v-tooltip>
                </v-btn-toggle>
            </v-layout>
            <br />

            

            <chart v-show="chartType === 'genderComposition'" :chartData="genderParliamentChartData" />
            <chart v-show="chartType === 'womenInParliament'" :chartData="womenInParliamentChartData" />
            <chart v-show="chartType === 'byParliament'" :chartData="byParliamentChartData" />

            <br/>
            <v-menu v-if="this.isMobile" d-inline  :close-on-content-click="true">
                <v-btn outline small slot="activator">
                    Sort <v-icon>sort</v-icon>
                </v-btn>
                <v-list>
                    <v-subheader>
                        Sort by:
                    </v-subheader>
                    <v-list-tile v-for="(item, index) in headers.filter(h => h.text != '' && h.sortable != false)" :key="item.value" @click="changeSort(item.value)">
                        <v-list-tile-title>
                            {{ item.text }}
                            <v-icon v-if="pagination.sortBy === item.value">{{pagination.descending ? 'arrow_downward':'arrow_upward'}}</v-icon>
                        </v-list-tile-title>
                    </v-list-tile>
                </v-list>
            </v-menu>
            <v-data-table
                :headers="headers" 
                :items="itemsReversed"
                :rows-per-page-items="rowsPerPageItems"
                :hide-actions="true"
                item-key="$id"
                :loading="loading"
                :pagination.sync="pagination"
                :class="{ mobileDataTable: isMobile, 'elevation-1': true }"
                :hide-headers="isMobile"
                :custom-sort="customSort"
                :expand="expand"
                :disable-initial-sort="true">
                <template v-slot:items="props">
                    <template v-if="isMobile">
                        <tr>
                            <td>
                                <div class="flex-content">
                                    <div class="flex-item">
                                        <h3><a @click="handleRecordClick(props.item.Parliament, null)">{{ numberToOrdinal(props.item.Parliament)}} Parliament (Opening: {{ props.item.Date | stringDateFormat }})</a></h3>
                                        <div class="flex-item" data-label="Parties">
                                            <div v-for="party in orderParties(props.item.Parties)" :key="party.$id">
                                                <span v-if="party.Party !== 'Total'">{{ party.Party }}: {{ party.WomenCount }} women/{{ party.MenCount }} men ({{party.Count}} total)</span>
                                                <span  class="font-weight-bold" v-if="party.Party == 'Total'">{{ party.Party }}: {{ props.item.Parties.find(p => p.Party === 'Total').Count }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </template>
                    <tr v-else @click="props.expanded = !props.expanded">
                        <td style="padding-right: 0"><v-icon>{{ props.expanded ? 'expand_less' : 'expand_more' }}</v-icon></td>
                        <td class="text-xs-left"><a @click="handleRecordClick(props.item.Parliament, null)">{{ props.item.Parliament }}</a></td>
                        <td class="text-xs-left">{{ props.item.Date | stringDateFormat }}</td>
                        <td class="text-xs-left">{{ props.item.Parties.find(p => p.Party === 'Total').WomenCount }} / {{ props.item.Parties.find(p => p.Party === 'Total').MenCount }}</td>
                        <td class="text-xs-left">{{props.item.Parties.find(p => p.Party === 'Total').Count}}</td>
                    </tr>
                </template>
                <template v-slot:expand="props">
                    <div class="pa-3">
                        <v-data-table :headers="[{ text: 'Party', value: 'Party'}, {text: 'Women', value: 'WomenCount' },
                             {text: 'Men', value: 'MenCount' }, {text: 'Total count', value: 'Count' }]"
                                      :disable-initial-sort="true"
                                      :hide-actions="true"
                                      :items="orderParties(props.item.Parties)">
                            <template v-slot:items="propsInner">
                                <tr>
                                    <td>{{ propsInner.item.Party }}</td>
                                    <td>{{ propsInner.item.WomenCount }}</td>
                                    <td>{{ propsInner.item.MenCount }} </td>
                                    <td>{{ propsInner.item.Count }}</td>
                                </tr>
                            </template>
                        </v-data-table>
                    </div>
                </template>
                <template v-slot:footer>
                </template>
            </v-data-table>
            <br/>
            <v-layout row wrap>
                <v-flex xs12>
                    <v-btn class="csvButton" @click="exportCsv" v-on:keyup.enter="exportCsv" color="primary">
                        <v-icon aria-hidden="true" :class="csvDownloadClass">{{csvDownloadIcon}}</v-icon>
                        <span>&nbsp;Download as CSV</span>
                    </v-btn>
                </v-flex>
            </v-layout>
        </template>
    </v-container>
</template>
<script>
import Loader from '@/components/Loader.vue';
import StatisticalHeader from './StatisticalHeader.vue';
import StatisticalMemberOrSenatorSelector from './StatisticalMemberOrSenatorSelector.vue';
import Chart from './Chart.vue';
import axios from 'axios';
import orderBy from 'lodash.orderby';
window.Handbook = window.Handbook || {}
const apiUri = window.Handbook.ApiUri || "https://localhandbookapi.aph.gov.au/";
axios.defaults.baseURL = apiUri;

export default {
    created() {
        this.initialiseItems();
    },
    components: {
        Loader,
        StatisticalHeader,
        StatisticalMemberOrSenatorSelector,
        Chart
    },
    methods: {
        orderParties(items) {
            return orderBy(items, [p => p.Party === 'Total' ? 0 : p.WomenCount], ['desc']);
        },
        initialiseItems() {
            this.loading = true;
            const parliamentarianType = this.selectedMpOrSenator.length == 0 || this.selectedMpOrSenator.length == 2 ?
                '' : this.selectedMpOrSenator[0];

            var loadingNumOfWomen = true;
            var loadingPartyWomen = true;

            const url = `StatisticalInformation/NumberOfWomenInParliament?parliamentariantype=${parliamentarianType}&committeeTypes=`;
            axios.get(`api/${url}`)
                .then(response => {
                    this.items = response.data.Rows;
                    this.parties = response.data.Parties;
                    this.partiesExcludingTotal = response.data.Parties.filter(p => p.Party !== 'Total');

                    this.headers = [
                        {
                            text: '',
                            value: 'Parliament',
                            width: '10px'
                        },
                        {
                            text: 'Parliament',
                            value: 'Parliament'
                        },
                        {
                            text: 'Parliament Opening',
                            value: 'Date'
                        },
                        {
                            text: 'Women/Men',
                            value: 'Total',
                            sortable: false
                        },
                        {
                            text: 'Total count',
                            value: 'Total'
                        }];

                    loadingNumOfWomen = false;
                    if (!loadingNumOfWomen && !loadingPartyWomen){
                        this.loading = false;
                    }
                });

            const partiesUrl = `StatisticalInformation/PartyRepresentationWomen?parliamentariantype=${parliamentarianType}`;
            axios.get(`api/${partiesUrl}`)
                .then(response => {
                    this.womenPartyItems = response.data.Rows;
                    this.womenParties = response.data.Parties;
                    loadingPartyWomen = false;
                    if (!loadingNumOfWomen && !loadingPartyWomen){
                        this.loading = false;
                    }
                });
        },
        handleRecordClick(parliament, partyId) {
            const query = {
                "logicalOperator":"all",
                "children":[
                ]};

            if (parliament) {
                query.children.push({"type":"query-builder-rule","query":{"rule":"parliament","selectedOperator":"of","selectedOperand":"Parliament","value":parliament}});
            }

            const selected = this.selectedMpOrSenator
            switch (selected) {
                case 'senator':
                    query.children.push({"type":"query-builder-rule","query":{"rule":"mpOrSenator","selectedOperand":"Member or Senator","value":"senator"}});
                    break;
                case 'mp':
                    query.children.push({"type":"query-builder-rule","query":{"rule":"mpOrSenator","selectedOperand":"Member or Senator","value":"member"}});
            }

            localStorage.query = JSON.stringify(query);    
            this.$router.push('/AdvancedSearch');
        },
        filterMpOrSenator() {
            this.initialiseItems();
        },
        customSort(items, index, isDesc) {
            items.sort((a, b) => {
                if (typeof index === "number") {
                    const partyA = a.Parties.find(p => p.PartyId === index);
                    const partyB = b.Parties.find(p => p.PartyId === index);
                    // for items, get party
                    // a is less than b is -1
                    // a is greater than b is 1
                    if (!isDesc) {
                        if (!partyA && !partyB) {
                            return 0;
                        }
                        if (!partyA) {
                            return -1
                        }
                        if (!partyB) {
                            return 1
                        }
                        return partyA.WomenCount < partyB.WomenCount ? -1 : 1;
                    } else {
                        if (!partyA && !partyB) {
                            return 0;
                        }
                        if (!partyA) {
                            return 1
                        }
                        if (!partyB) {
                            return -1
                        }
                        return partyB.WomenCount < partyA.WomenCount ? -1 : 1;
                    }
                } else if (index === 'Other' || index === 'Total') {
                    const partyA = a.Parties.find(p => p.Party === index);
                    const partyB = b.Parties.find(p => p.Party === index);
                    // for items, get party
                    // a is less than b is -1
                    // a is greater than b is 1
                    if (!isDesc) {
                        if (!partyA && !partyB) {
                            return 0;
                        }
                        if (!partyA) {
                            return -1
                        }
                        if (!partyB) {
                            return 1
                        }
                        return partyA.WomenCount < partyB.WomenCount ? -1 : 1;
                    } else {
                        if (!partyA && !partyB) {
                            return 0;
                        }
                        if (!partyA) {
                            return 1
                        }
                        if (!partyB) {
                            return -1
                        }
                        return partyB.WomenCount < partyA.WomenCount ? -1 : 1;
                    }
                } else {
                    if (!isDesc) {
                        return a[index] < b[index] ? -1 : 1;
                    } else {
                        return b[index] < a[index] ? -1 : 1;
                    }
                }
            });
            return items;
        },        
        exportCsv: function () {
            this.csvDownloading = true;
            const records = this.items
                .map(i => ({ Parliament: i.Parliament, Date: new Date(i.Date).toLocaleDateString(), ...i.Parties.reduce((ac,a) => ({...ac,[a.Party]: a.WomenCount }),{}) }));
            let headers = this.headers.map(h => h.text);
            const filename = this.title;
            const comment = `Data current as of ${(new Date()).toLocaleDateString()}`;
            this.$store.dispatch('GETCSV', { DATA: records, HEADERS: headers, FILENAME: filename, COMMENT: comment })
                .catch(this.showError)
                .finally(() => this.csvDownloading = false);
        },
        onResize() {
            if (window.innerWidth < 769){
                this.isMobile = true;
            }
            else{
                this.isMobile = false;
            }
        },
        changeSort(column) {
            if (this.pagination.sortBy === column) {
                this.pagination.descending = !this.pagination.descending
            } 
            else {
                this.pagination.sortBy = column
                this.pagination.descending = false
            }
        },
    }, 
    computed: {
            csvDownloadIcon() {
                return this.csvDownloading ? 'cached' : 'text_snippet';
            },
            csvDownloadClass() {
                let csvClass = 'mr-1';
                if (this.csvDownloading){
                    csvClass = csvClass + ' loading'
                }
                return csvClass;
            },
            itemsReversed() {
                return [...this.items].reverse();
            },
            byParliamentChartData() {
               const series = this
                    .womenParties
                    .filter(p => p.PartyId)
                    .map((h, index) => ({
                        name: h.Party,
                        color: h.PartyColour,
                        data: [],
                        id: h.PartyId,
                        showInLegend: true,
                    }));
                const categories = [];

                const items = this.womenPartyItems.slice();

                items.forEach(d => {
                    categories.push(d.Parliament);
                    // for each row, add into the series
                    series.forEach(s => {
                        // find the right party
                        const party = d.Parties.find(p => p.PartyId === s.id);
                        if (party) {
                            s.data.push(party.Count);
                        } else {
                            s.data.push(null);
                        }
                    });
                });

                return {
                    chart: {
                        type: 'column',
                        height: 700,
                    },
                    title: {
                        text: 'Women in Parliament by party in each Parliament'
                    },
                    exporting: {
                        filename: 'Women in Parliament by party in each Parliament'
                    },
                   xAxis: {
                        categories: categories,
                        tickmarkPlacement: 'on',
                        title: {
                            text: 'Parliament Number'
                        }
                    },
                    yAxis: {
                        title: {
                            enabled: false
                        },
                        endOnTick: false,
                        gridlines: true,
                        gridlineColor: "#333",
                        gridLineWidth: 3
                    },
                    plotOptions: {
                        column: {
                            stacking: 'normal',
                        }
                    },
                    // one series per party, one item per year.
                   series
                }
            },
            genderParliamentChartData() {
               const series = ['Men', 'Women']
                    .map(h => ({
                    name: h,
                    data: [],
                }));

                const categories = [];
                var data = this.items;

                data.forEach(d => {
                    categories.push(d.Parliament);
                    const male = series[0];
                    male.color = '#8085e9';
                    const female = series[1];
                    female.color = '#f7a35c';
                    const total = d.Parties.find(p => p.Party === 'Total');
                    female.data.push(total.WomenCount);
                    male.data.push(total.Count - total.WomenCount);
                });

                return {
                    chart: {
                        type: 'column',
                        height: 400,
                    },
                    title: {
                        text: 'Gender composition in each Parliament'
                    },
                    exporting: {
                        filename: 'Gender composition in each Parliament'
                    },
                   xAxis: {
                        categories: categories,
                        tickmarkPlacement: 'on',
                        title: {
                            text: 'Parliament Number'
                        }
                    },
                    yAxis: {
                        title: {
                            enabled: false
                        },
                        endOnTick: false,
                        gridlines: true,
                        gridlineColor: "#333",
                        gridLineWidth: 3
                    },
                    plotOptions: {
                        column: {
                            stacking: 'normal',
                        }
                    },
                    // one series per party, one item per year.
                   series
                }
            },
            womenInParliamentChartData() {
                const series = ['Men', 'Women']
                    .map(h => ({
                    name: h,
                    data: [],
                }));

                const categories = [];
                var data = this.items;

                data.forEach(d => {
                    categories.push(d.Parliament);
                   const male = series[0];
                    male.color = '#8085e9';
                    const female = series[1];
                    female.color = '#f7a35c';
                    const total = d.Parties.find(p => p.Party === 'Total');
                    female.data.push(total.WomenCount);
                    male.data.push(total.Count - total.WomenCount);
                });

                return {
                    chart: {
                        type: 'area'
                    },
                    title: {
                        text: this.title
                    },
                    accessibility: {
                        point: {
                            valueDescriptionFormat: '{index}. {point.category}, {point.y:,.0f}, {point.percentage:.1f}%.'
                        }
                    },
                    exporting: {
                        filename: 'Gender composition in each Parliament'
                    },
                    xAxis: {
                        categories: categories,
                        tickmarkPlacement: 'on',
                        title: {
                            enabled: false
                        }
                    },
                    yAxis: {
                        labels: {
                            format: '{value}%'
                        },
                        title: {
                            enabled: false
                        }
                    },
                    tooltip: {
                        pointFormat: '<span style="color:{series.color}">{series.name}</span>: <b>{point.percentage:.1f}%</b> ({point.y:,.0f})<br/>',
                        split: true
                    },
                    plotOptions: {
                        area: {
                            stacking: 'percent',
                            lineColor: '#ffffff',
                            lineWidth: 1,
                            marker: {
                                lineWidth: 1,
                                lineColor: '#ffffff'
                            }
                        }
                    },
                    series
                }
            },
    },
    watch: {
        selectedMpOrSenator: function () {
            this.initialiseItems();
        }
    },
    mounted() {
        this.onResize();
    },
    data() {
        return {
        title: 'Gender composition in each Parliament',
        isMobile: false,
        headers: [],
        items: [],
        womenPartyItems: [],
        womenCommitteeItems: [],
        womenCommitteeParties: [],
        parties: [],
        chartType: 'genderComposition',
        expand: false,
        partiesExcludingTotal: [],
        search: '',
        loading: true,
        pagination: {
            rowsPerPage: -1,
            sortBy: '',
            descending: false
        },
        csvDownloading: false,
        selectedMpOrSenator: ['senator', 'mp'],
        rowsPerPageItems: [ {
            "text": "$vuetify.dataIterator.rowsPerPageAll",
            "value": -1
            }]
        }
    }
}
</script>
<style scoped>
    .handbook_toggleButton {
        flex-wrap: wrap;
    }
</style>